import { cva } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '@/utils';

const otpInputVariants = cva(
  'flex h-14 w-14 items-center justify-center rounded-lg border-2 text-center text-2xl font-semibold transition-all duration-200 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50',
  {
    variants: {
      variant: {
        default:
          'border-magical-moonlight-d1 text-magical-moonlight-dark focus:border-bhanzu-orange focus:ring-2 focus:ring-bhanzu-orange/20',
        error:
          'border-fusion-red text-magical-moonlight-dark focus:border-fusion-red focus:ring-2 focus:ring-fusion-red/20',
        success:
          'border-success-green text-magical-moonlight-dark focus:border-success-green focus:ring-2 focus:ring-success-green/20',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

export interface OTPInputProps {
  /** Number of OTP input boxes (default: 4) */
  length?: number;
  /** Callback when OTP is complete */
  onComplete?: (otp: string) => void;
  /** Callback on OTP change */
  onChange?: (otp: string) => void;
  /** Visual variant */
  variant?: 'default' | 'error' | 'success';
  /** Current OTP value (for controlled component) */
  value?: string;
  /** Disabled state */
  disabled?: boolean;
  /** Custom className for container */
  className?: string;
  /** Custom className for each input box */
  inputClassName?: string;
  /** Auto-focus first input on mount */
  autoFocus?: boolean;
}

const OTPInput = React.forwardRef<HTMLDivElement, OTPInputProps>(
  (
    {
      length = 4,
      onComplete,
      onChange,
      variant = 'default',
      value = '',
      disabled = false,
      className,
      inputClassName,
      autoFocus = false,
    },
    ref
  ) => {
    const [otp, setOtp] = React.useState<string[]>(
      Array.from({ length }, () => '')
    );
    const inputRefs = React.useRef<(HTMLInputElement | null)[]>([]);

    // Update OTP state when controlled value changes
    React.useEffect(() => {
      if (value != null) {
        const otpArray = [...value].slice(0, length);
        setOtp([
          ...otpArray,
          ...Array.from({ length: length - otpArray.length }, () => ''),
        ]);
      }
    }, [value, length]);

    // Auto-focus first input on mount
    React.useEffect(() => {
      if (autoFocus && inputRefs.current[0]) {
        inputRefs.current[0].focus();
      }
    }, [autoFocus]);

    const handleChange = (index: number, value: string) => {
      // Only allow numeric input
      if (value && !/^\d$/.test(value)) {
        return;
      }

      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);

      // Notify parent of change
      const otpString = newOtp.join('');
      onChange?.(otpString);

      // Auto-focus next input if value is entered
      if (value && index < length - 1) {
        inputRefs.current[index + 1]?.focus();
      }

      // Trigger onComplete if all boxes are filled
      if (otpString.length === length && !otpString.includes('')) {
        onComplete?.(otpString);
      }
    };

    const handleKeyDown = (
      index: number,
      e: React.KeyboardEvent<HTMLInputElement>
    ) => {
      // Move to previous input on backspace if current is empty
      if (e.key === 'Backspace' && !otp[index] && index > 0) {
        inputRefs.current[index - 1]?.focus();
      }

      // Move to previous/next input with arrow keys
      if (e.key === 'ArrowLeft' && index > 0) {
        e.preventDefault();
        inputRefs.current[index - 1]?.focus();
      }

      if (e.key === 'ArrowRight' && index < length - 1) {
        e.preventDefault();
        inputRefs.current[index + 1]?.focus();
      }
    };

    const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
      e.preventDefault();
      const pastedData = e.clipboardData.getData('text/plain');

      // Only process if pasted data contains only digits
      if (!/^\d+$/.test(pastedData)) {
        return;
      }

      const pastedOtp = [...pastedData].slice(0, length);
      const newOtp = [
        ...pastedOtp,
        ...Array.from({ length: length - pastedOtp.length }, () => ''),
      ];
      setOtp(newOtp);

      // Notify parent of change
      const otpString = newOtp.join('');
      onChange?.(otpString);

      // Focus the next empty input or the last input
      const nextEmptyIndex = newOtp.findIndex(digit => !digit);
      const focusIndex = nextEmptyIndex === -1 ? length - 1 : nextEmptyIndex;
      inputRefs.current[focusIndex]?.focus();

      // Trigger onComplete if all boxes are filled
      if (otpString.length === length && !otpString.includes('')) {
        onComplete?.(otpString);
      }
    };

    const handleFocus = (index: number) => {
      // Select the input content on focus for easy replacement
      inputRefs.current[index]?.select();
    };

    return (
      <div
        ref={ref}
        className={cn('flex gap-3 items-center justify-center', className)}
        role="group"
        aria-label="One-time password input"
      >
        {Array.from({ length }, (_, index) => (
          <input
            key={index}
            ref={el => {
              inputRefs.current[index] = el;
            }}
            type="text"
            inputMode="numeric"
            pattern="\d{1}"
            maxLength={1}
            value={otp[index]}
            onChange={e => handleChange(index, e.target.value)}
            onKeyDown={e => handleKeyDown(index, e)}
            onPaste={handlePaste}
            onFocus={() => handleFocus(index)}
            disabled={disabled}
            className={cn(otpInputVariants({ variant }), inputClassName)}
            aria-label={`Digit ${index + 1} of ${length}`}
            autoComplete={index === 0 ? 'one-time-code' : 'off'}
          />
        ))}
      </div>
    );
  }
);

OTPInput.displayName = 'OTPInput';

export default OTPInput;
export { otpInputVariants };
